// app/shared/components/marketing/FeaturedCardComponent.tsx
'use client';

import { ArrowUpRight } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

import { cn } from '@/lib/utils';

import {
  accentBgClasses,
  AccentColor,
  accentHoverBgClasses,
} from '../../utils/accentColors';

interface FeaturedCardMetric {
  value: string;
  label: string;
}

interface FeaturedCardProps {
  title: string;
  description: string;
  href: string;
  accentColor: AccentColor;
  metric?: FeaturedCardMetric;
  logoUrl?: string;
  logoFallback?: string; // Single character fallback if no logo
  inverted?: boolean; // Swap layout - content left, image right OR image left, content right
  className?: string;
}

export function FeaturedCardComponent({
  title,
  description,
  href,
  accentColor,
  metric,
  logoUrl,
  logoFallback,
  inverted = false,
  className,
}: FeaturedCardProps) {
  const ContentSection = (
    <div className="p-10 md:p-12 flex flex-col justify-between bg-surface-light">
      <div>
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-5">
          {title}
        </h2>
        <p className="text-xl text-gray-700 leading-relaxed mb-6">
          {description}
        </p>
      </div>

      {/* Bottom metrics row */}
      <div className="flex items-end justify-between border-t border-dashed border-gray-300 pt-6">
        <div>
          {metric && (
            <>
              <span className="text-5xl md:text-6xl font-bold text-black block leading-none">
                {metric.value}
              </span>
              <p className="text-lg text-gray-500 mt-1">{metric.label}</p>
            </>
          )}
        </div>
        <div
          className={cn(
            'w-14 h-14 rounded-xl border border-gray-300 flex items-center justify-center transition-all duration-300',
            accentHoverBgClasses[accentColor],
            'group-hover:border-transparent'
          )}
        >
          <ArrowUpRight
            size={26}
            className="text-gray-500 group-hover:text-white transition-colors"
          />
        </div>
      </div>
    </div>
  );

  const VisualSection = (
    <div
      className={cn(
        'p-10 flex items-center justify-center min-h-[400px]',
        accentBgClasses[accentColor]
      )}
    >
      {logoUrl ? (
        <div className="rounded-2xl overflow-hidden max-w-[320px] bg-surface-light/10 p-4">
          <img
            src={logoUrl}
            alt={title}
            className="w-full h-auto object-contain"
          />
        </div>
      ) : (
        <span
          className="font-bold text-white/20"
          style={{ fontSize: 'clamp(6rem, 15vw, 10rem)' }}
        >
          {logoFallback || title.charAt(0)}
        </span>
      )}
    </div>
  );

  return (
    <Link
      href={href}
      className={cn(
        'group block rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300',
        className
      )}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
        {inverted ? (
          <>
            {VisualSection}
            {ContentSection}
          </>
        ) : (
          <>
            {ContentSection}
            {VisualSection}
          </>
        )}
      </div>
    </Link>
  );
}

